'use client';

import {
  Button,
  Card,
  CardContent,
  cn,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Label,
  RadioGroup,
  RadioGroupItem,
} from '@up2stay/up2promo-ui';
import { Icon } from '@iconify/react';
import { useDialogManager } from '@up2promo-setup-frontend/core/hooks/use-dialog-manager';
import { useIntercom } from '@up2promo-setup-frontend/core/hooks/use-intercom';
import { useSetupStore } from '@up2promo-setup-frontend/core/store/setup.store';
import { ProductType } from '@up2promo-setup-frontend/core/types/product-type.enum';
import { dictByType } from '@up2promo-setup-frontend/core/utils/dict-by-type';
import { buildSetupProductBody } from '@up2promo-setup-frontend/core/utils/product-type-api';
import { fadeInUp } from '@up2promo-setup-frontend/ui/animations/fade-in-up.animation';
import MessageModal from '@up2promo-setup-frontend/ui/components/modals/message.modal';
import {
  useDictionary,
  useLocale,
} from '@up2promo-setup-frontend/ui/providers/dictionary.provider';
import { ProductsPublicApi } from '@up2stay/up2promo-api-client';
import { motion } from 'motion/react';
import { ChevronDown } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ButtonWithEnter from '../components/button-with-enter';
import { SetupStepShell } from '../components/setup-step-shell';
import { useSetupNavigation } from './hooks/use-setup-navigation';

export const CustomRadio = (props: {
  value: string;
  description?: string;
  children: React.ReactNode;
}) => {
  const { children, description, value } = props;

  return (
    <Label
      htmlFor={value}
      className={cn(
        'flex w-full cursor-pointer items-start gap-3 rounded-lg border border-border bg-card p-4 transition-colors',
        'hover:bg-accent has-[:checked]:border-primary',
      )}
    >
      <RadioGroupItem value={value} id={value} className="mt-0.5 shrink-0" />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="text-sm font-medium text-foreground sm:text-base">
          {children}
        </span>
        {description && (
          <span className="text-xs text-foreground/65 sm:text-sm">
            {description}
          </span>
        )}
      </div>
    </Label>
  );
};

export const ContactCard = (props: {
  text: string;
  icon: string;
  href: string;
}) => {
  return (
    <Card className="w-full md:max-w-[300px] lg:max-w-[400px]">
      <a href={props.href} target="_blank" rel="noopener noreferrer">
        <CardContent className="flex flex-row items-center gap-2 p-4">
          <Icon className="text-3xl text-foreground" icon={props.icon} />
          <p className="text-sm text-foreground">{props.text}</p>
        </CardContent>
      </a>
    </Card>
  );
};

const ContactCards = () => {
  const translations = useDictionary();

  return (
    <div className="flex flex-col gap-3">
      <ContactCard
        text={`${translations.pages.suggestions.notFound.contactOptions.phone}: +49 2461 9160861`}
        icon="material-symbols:phone-in-talk-rounded"
        href="tel:+4924619160861"
      />
      <ContactCard
        text={`${translations.pages.suggestions.notFound.contactOptions.email}: contact@up2promo.com`}
        icon="material-symbols:mail"
        href="mailto:contact@up2promo.com"
      />
      <ContactCard
        text={
          translations.pages.suggestions.notFound.contactOptions
            .liveChatAndContactForm
        }
        icon="material-symbols:chat-rounded"
        href="https://www.up2promo.com/pages/contact"
      />
    </div>
  );
};

export default function SelectSuggestionPage() {
  const translations = useDictionary();
  const locale = useLocale();
  const { goNext, navigateTo } = useSetupNavigation();

  // Use selectors to only subscribe to needed values
  const suggestions = useSetupStore((state) => state.suggestions);
  const type = useSetupStore((state) => state.type);
  const input = useSetupStore((state) => state.input);
  const email = useSetupStore((state) => state.email);
  const hash = useSetupStore((state) => state.hash);
  const setTargetLink = useSetupStore((state) => state.setTargetLink);
  const setType = useSetupStore((state) => state.setType);
  const setInput = useSetupStore((state) => state.setInput);
  const setSuggestions = useSetupStore((state) => state.setSuggestions);

  const { openDialog } = useDialogManager();

  const intercom = useIntercom({
    user: hash ? { hash, email } : email ? { email } : undefined,
    enabled: true,
  });

  // Track when suggestions are shown or when no suggestions found
  useEffect(() => {
    if (intercom.isBooted()) {
      if (suggestions && suggestions.length > 0) {
        intercom.trackEvent('suggestions-shown', {
          hash,
          product_type: type,
          suggestions_count: suggestions.length,
          has_email: !!email,
          input_preview: input
            ? input.length > 50
              ? input.substring(0, 50) + '...'
              : input
            : undefined,
          auto_selected: suggestions.length === 1,
        });
      } else if (suggestions && suggestions.length === 0) {
        intercom.trackEvent('no-suggestions-found', {
          hash,
          product_type: type,
          suggestions_count: 0,
          has_email: !!email,
          input_preview: input
            ? input.length > 50
              ? input.substring(0, 50) + '...'
              : input
            : undefined,
        });
      }
    }
  }, [suggestions, intercom, hash, type, email, input]);

  // Calculate selected suggestion ID during render (not in Effect)
  const autoSelectedId = useMemo(() => {
    if (suggestions?.length === 1) {
      return suggestions[0]!.id;
    }
    return '';
  }, [suggestions]);

  const [selectedSuggestionIdState, setSelectedSuggestionIdState] =
    useState<string>(autoSelectedId);

  // Final selected ID: use manual selection if set, otherwise use auto-selected
  // FIX: Default to empty string instead of undefined to fix controlled/uncontrolled warning
  const finalSelectedId = selectedSuggestionIdState || autoSelectedId || '';

  const [isLoading, setLoading] = useState(false);

  const handleSwitchToCustom = useCallback(() => {
    // Track switch to custom from suggestions
    if (intercom.isBooted()) {
      intercom.trackEvent('switched-to-custom-from-suggestions', {
        hash,
        from_type: type,
        to_type: ProductType.CUSTOM,
        suggestions_count: suggestions?.length || 0,
        had_selection: !!finalSelectedId,
        has_email: !!email,
      });
    }

    setType(ProductType.CUSTOM);
    setInput(undefined);
    setSuggestions(undefined);
    navigateTo.customUrl();
  }, [
    setType,
    setInput,
    setSuggestions,
    navigateTo,
    intercom,
    hash,
    type,
    suggestions,
    finalSelectedId,
    email,
  ]);

  const createProduct = useCallback(async () => {
    try {
      if (!finalSelectedId) return;

      const selectedSuggestion = suggestions?.find(
        (place) => place.id === finalSelectedId,
      );

      if (!selectedSuggestion) return;

      setLoading(true);

      const targetUrl = selectedSuggestion.targetLink;

      // Track suggestion selection
      if (intercom.isBooted()) {
        intercom.trackEvent('suggestion-selected', {
          hash,
          product_type: type,
          suggestion_id: selectedSuggestion.id,
          suggestion_name: selectedSuggestion.name,
          suggestion_index:
            suggestions?.findIndex((s) => s.id === finalSelectedId) ?? -1,
          total_suggestions: suggestions?.length || 0,
          was_auto_selected: suggestions?.length === 1,
          target_url:
            targetUrl.length > 200
              ? targetUrl.substring(0, 200) + '...'
              : targetUrl,
          has_email: !!email,
        });
      }

      // Public product setup (no workspace); the contact gets the reset mail
      await ProductsPublicApi.setupProduct({
        body: buildSetupProductBody({
          hash: hash!,
          url: targetUrl,
          type,
          email,
          locale,
        }),
      });

      setTargetLink(targetUrl);

      goNext('select-suggestion');
    } catch {
      // Track error
      if (intercom.isBooted()) {
        intercom.trackEvent('suggestion-error', {
          hash,
          product_type: type,
          error_type: 'link_mapping_creation_failed',
          suggestion_id: finalSelectedId,
          has_email: !!email,
        });
      }

      void openDialog(MessageModal, {
        componentProps: {
          title: translations.pages.suggestions.error.title,
          bodyText: translations.pages.suggestions.error.description,
          isDestructive: true,
        },
      });
    } finally {
      setLoading(false);
    }
  }, [
    finalSelectedId,
    suggestions,
    email,
    hash,
    locale,
    setTargetLink,
    goNext,
    openDialog,
    translations,
    setLoading,
    intercom,
    type,
  ]);

  return (
    <SetupStepShell column="wide">
      <div className="flex flex-col gap-2 sm:gap-3">
        {suggestions && suggestions.length >= 1 && (
          <>
            <motion.h1
              custom={0}
              initial="hidden"
              animate="visible"
              variants={fadeInUp}
              className="text-2xl font-semibold tracking-tight text-foreground text-balance md:text-3xl lg:text-4xl"
            >
              {dictByType(
                translations.pages.suggestions.found.titleByType,
                type as ProductType,
              )}
            </motion.h1>
            <motion.p
              custom={1}
              initial="hidden"
              animate="visible"
              variants={fadeInUp}
              className="max-w-prose text-sm text-foreground/70 text-pretty md:text-base"
            >
              {translations.pages.suggestions.found.description}
            </motion.p>

            <motion.div
              custom={2}
              initial="hidden"
              animate="visible"
              variants={fadeInUp}
              className="my-2 flex flex-col gap-2 sm:my-4 md:my-6"
            >
              <p className="text-sm font-medium text-foreground">
                {translations.pages.suggestions.found.suggestionsTitle}
              </p>
              <RadioGroup
                value={finalSelectedId}
                onValueChange={(value) => {
                  setSelectedSuggestionIdState(value);
                  // Track manual selection change
                  if (intercom.isBooted() && value !== autoSelectedId) {
                    const selectedSuggestion = suggestions?.find(
                      (s) => s.id === value,
                    );
                    intercom.trackEvent('suggestion-manually-selected', {
                      hash,
                      product_type: type,
                      suggestion_id: value,
                      suggestion_name: selectedSuggestion?.name,
                      was_auto_selected: false,
                      total_suggestions: suggestions?.length || 0,
                    });
                  }
                }}
                className="grid grid-cols-1 md:grid-cols-2 w-full gap-2"
              >
                {suggestions?.map((place) => (
                  <CustomRadio
                    description={place.description}
                    key={place.id}
                    value={place.id}
                  >
                    {place.name}
                  </CustomRadio>
                ))}
              </RadioGroup>
            </motion.div>

            <motion.div
              custom={3}
              initial="hidden"
              animate="visible"
              variants={fadeInUp}
            >
              <Collapsible
                onOpenChange={(open) => {
                  if (open && intercom.isBooted()) {
                    intercom.trackEvent('contact-cards-opened', {
                      hash,
                      product_type: type,
                      suggestions_count: suggestions?.length || 0,
                      has_selection: !!finalSelectedId,
                      has_email: !!email,
                    });
                  }
                }}
              >
                <CollapsibleTrigger className="group flex w-full items-center justify-between rounded-lg border border-border bg-card p-4 text-left data-[state=open]:[&_svg]:rotate-180">
                  <div className="flex flex-col gap-1">
                    <span className="font-medium text-foreground">
                      {
                        translations.pages.suggestions.found.noMatchAccordion
                          .title
                      }
                    </span>
                    <span className="text-sm text-foreground/70">
                      {
                        translations.pages.suggestions.found.noMatchAccordion
                          .subtitle
                      }
                    </span>
                  </div>
                  <ChevronDown className="h-4 w-4 shrink-0 text-foreground transition-transform duration-200" />
                </CollapsibleTrigger>
                <CollapsibleContent className="pt-4">
                  <ContactCards />
                </CollapsibleContent>
              </Collapsible>
            </motion.div>

            <motion.div
              custom={4}
              initial="hidden"
              animate="visible"
              variants={fadeInUp}
            >
              <ButtonWithEnter
                disabled={!finalSelectedId}
                text={translations.pages.suggestions.found.button}
                onClick={createProduct}
                isLoading={isLoading}
              />
            </motion.div>

            <motion.div
              custom={5}
              initial="hidden"
              animate="visible"
              variants={fadeInUp}
              className="mt-4 sm:mt-6"
            >
              <Button
                type="button"
                variant="ghost"
                onClick={handleSwitchToCustom}
              >
                {translations.pages.customUrlStep.switchToCustom}
              </Button>
            </motion.div>
          </>
        )}

        {suggestions && suggestions.length === 0 && (
          <>
            <motion.h1
              custom={0}
              initial="hidden"
              animate="visible"
              variants={fadeInUp}
              className="text-2xl font-semibold tracking-tight text-foreground text-balance md:text-3xl lg:text-4xl"
            >
              {translations.pages.suggestions.notFound.title}
            </motion.h1>
            <motion.p
              custom={1}
              initial="hidden"
              animate="visible"
              variants={fadeInUp}
              className="mb-2 max-w-prose text-sm text-foreground/70 text-pretty sm:mb-3 md:text-base"
            >
              {translations.pages.suggestions.notFound.description}
            </motion.p>

            <ContactCards />

            <motion.div
              className="mt-3"
              custom={3}
              initial="hidden"
              animate="visible"
              variants={fadeInUp}
            >
              <ButtonWithEnter
                text={translations.pages.suggestions.notFound.button}
                onClick={() => {
                  // Track going back from no suggestions found
                  if (intercom.isBooted()) {
                    intercom.trackEvent('no-suggestions-back', {
                      hash,
                      product_type: type,
                      input_preview: input
                        ? input.length > 50
                          ? input.substring(0, 50) + '...'
                          : input
                        : undefined,
                      has_email: !!email,
                    });
                  }
                  // Navigate back to input step
                  navigateTo.input();
                }}
                autoFocus
              />
            </motion.div>
          </>
        )}
      </div>
    </SetupStepShell>
  );
}
